/**
 * Astrology calendar events for the event promo modal.
 * `advisorName` must match `advisorsList[].name` exactly.
 */
export interface AstrologyEvent {
  /** Display date range, e.g. "Jun 19 to Jul 12, 2026". */
  date: string
  /** Inclusive ISO date (YYYY-MM-DD) for selection. */
  startDate: string
  /** Inclusive ISO date (YYYY-MM-DD) for selection. */
  endDate: string
  title: string
  category: string
  description: string
  advisorName: string
  advisorMessage: string
}

export const ASTROLOGY_EVENTS: AstrologyEvent[] = [
  {
    date: "Jun 19 to Jul 12, 2026",
    startDate: "2026-06-19",
    endDate: "2026-07-12",
    title: "Mercury Retrograde Begins",
    category: "Mercury Rx",
    description:
      "Mercury slows and reverses for three weeks. Conversations may need to be repeated, plans may shift, and old messages may resurface. This isn't a moment to push forward — it's a moment to revisit and refine.",
    advisorName: "Sanjay Kapoor",
    advisorMessage:
      "Tarot's calling for the next three weeks. Pull a card with me before you send that text you might regret",
  },
  {
    date: "Jun 21, 2026",
    startDate: "2026-06-21",
    endDate: "2026-06-21",
    title: "Summer Solstice",
    category: "Solstice",
    description:
      "The longest day of the year marks a turning point. Whatever you've been building since spring reaches its peak now. The next six months will test what you've planted — but the energy today is yours to claim.",
    advisorName: "Claire Bennett",
    advisorMessage:
      "Solstices are good for big questions. What's the one decision you've been circling?",
  },
  {
    date: "Jul 10, 2026",
    startDate: "2026-07-10",
    endDate: "2026-07-10",
    title: "Full Moon in Capricorn — Buck Moon",
    category: "Full Moon",
    description:
      "The full moon in Capricorn brings work, structure, and long-term goals into sharp focus. Something you've been building is asking for completion — or for honest reassessment. What's the next right step?",
    advisorName: "Meera Rajan",
    advisorMessage:
      "This Full Moon is doing serious work in your career sector. Want me to pull up your chart and see what's worth pushing on right now?",
  },
  {
    date: "Jul 12, 2026",
    startDate: "2026-07-12",
    endDate: "2026-07-12",
    title: "Mercury Goes Direct",
    category: "Mercury Direct",
    description:
      "After three weeks of backward motion, Mercury moves forward again. Conversations that stalled can resume, decisions that felt frozen can be made. The clarity you've been waiting for is arriving.",
    advisorName: "Sanjay Kapoor",
    advisorMessage:
      "The pause is over. Tell me what you've been holding back — let's see if it still needs saying",
  },
  {
    date: "Jul 29, 2026",
    startDate: "2026-07-29",
    endDate: "2026-07-29",
    title: "Full Moon in Aquarius",
    category: "Full Moon",
    description:
      "The Aquarius full moon sparks revolution — within and around you. A truth you've been suppressing wants out. A pattern you've outgrown wants to break. Watch what surfaces over the next 48 hours.",
    advisorName: "Mason Taylor",
    advisorMessage:
      "Aquarius full moons crack things open. No judgment if it's been a weird week — want to look at what's actually shifting?",
  },
  {
    date: "Aug 8, 2026",
    startDate: "2026-08-08",
    endDate: "2026-08-08",
    title: "Lion's Gate Portal Opens",
    category: "Portal",
    description:
      "Sirius rises with the Sun, opening what many traditions call a portal of manifestation. Whatever you focus your intention on today carries unusual weight. The next 40 days will show how it unfolds.",
    advisorName: "Arjun Verma",
    advisorMessage:
      "8/8 is a window. Tell me what you're trying to bring in this year and i'll show you what your chart says about how to do it",
  },
  {
    date: "Aug 12, 2026",
    startDate: "2026-08-12",
    endDate: "2026-08-12",
    title: "Total Solar Eclipse in Leo",
    category: "Eclipse",
    description:
      "A rare total eclipse asks what you're ready to leave behind. Eclipses don't suggest gentle shifts — they tend to mark turning points that become clear only in retrospect. The next six months will show what this one set in motion.",
    advisorName: "Arjun Verma",
    advisorMessage:
      "This eclipse is one of the biggest of the cycle. Drop me a line — i'll walk you through what it's lighting up in your chart",
  },
  {
    date: "Aug 28, 2026",
    startDate: "2026-08-28",
    endDate: "2026-08-28",
    title: "Partial Lunar Eclipse in Pisces",
    category: "Eclipse",
    description:
      "The second eclipse in two weeks. Pisces brings the unconscious to the surface — dreams, intuitions, half-spoken truths. Pay attention to what comes up. It's been waiting a while.",
    advisorName: "Sophie Carter",
    advisorMessage:
      "Pisces eclipses can leave you feeling a bit off. Let me read your aura — sometimes the heaviness has a clear source you just haven't named",
  },
  {
    date: "Sep 23, 2026",
    startDate: "2026-09-23",
    endDate: "2026-09-23",
    title: "Autumn Equinox",
    category: "Equinox",
    description:
      "Day and night stand equal. The energy turns inward as Libra season begins. This is the natural moment to evaluate balance — in relationships, in work, in how you spend your time. What needs rebalancing?",
    advisorName: "Brooke Hayes",
    advisorMessage:
      "Equinox is balance check-in. How's your love life feeling, honestly? Let's look at what's tilting",
  },
  {
    date: "Sep 26, 2026",
    startDate: "2026-09-26",
    endDate: "2026-09-26",
    title: "Full Moon in Aries — Harvest Moon",
    category: "Full Moon",
    description:
      "The Aries full moon is fire meeting completion. Something you've been holding back wants to be expressed — boldly. The risk: impulsive action. The gift: clarity about what you actually want.",
    advisorName: "Mason Taylor",
    advisorMessage:
      "Aries fire wants action — but the cards can tell us if it's the right action or just the loudest one. Wanna pull?",
  },
  {
    date: "Oct 8 to Oct 28, 2026",
    startDate: "2026-10-08",
    endDate: "2026-10-28",
    title: "Mercury Retrograde Returns",
    category: "Mercury Rx",
    description:
      "Mercury reverses again — this time in Libra and Scorpio. Old partnerships, unfinished conversations, hidden truths may resurface. The next three weeks favor revision over launching.",
    advisorName: "Sanjay Kapoor",
    advisorMessage:
      "Round two of Mercury Rx — this time hitting deeper stuff. Relationships, secrets, what's been left unsaid. Ready to lay the cards?",
  },
  {
    date: "Oct 25, 2026",
    startDate: "2026-10-25",
    endDate: "2026-10-25",
    title: "Full Moon in Taurus — Hunter's Moon",
    category: "Full Moon",
    description:
      "The Taurus full moon grounds us in the body — money, food, comfort, the senses. What does your life actually feel like? Where are you starving yourself of something essential?",
    advisorName: "Kavita Sharma",
    advisorMessage:
      "Taurus moon is asking about money and what makes you feel safe. Your numbers are showing something interesting this week — want to see?",
  },
  {
    date: "Oct 28, 2026",
    startDate: "2026-10-28",
    endDate: "2026-10-28",
    title: "Mercury Goes Direct (Second Time)",
    category: "Mercury Direct",
    description:
      "Mercury straightens out for the final stretch of the year. The work of revision is done — now you can speak, send, decide. What's been waiting in your drafts folder?",
    advisorName: "Sanjay Kapoor",
    advisorMessage:
      "Second time Mercury straightens out this year. Let's check if what you've been working on is ready to ship",
  },
  {
    date: "Oct 31, 2026",
    startDate: "2026-10-31",
    endDate: "2026-10-31",
    title: "Samhain — The Veil Thins",
    category: "Portal",
    description:
      "The cross-quarter day between equinox and solstice — the moment in the wheel of the year when ancestors feel closest. What old story do you carry? What part of yourself is ready to be remembered?",
    advisorName: "Dylan Brooks",
    advisorMessage:
      "Samhain is when the veil's thin. If a pattern keeps showing up — from family, past relationships, whatever — let's look at where it actually started",
  },
  {
    date: "Nov 24, 2026",
    startDate: "2026-11-24",
    endDate: "2026-11-24",
    title: "Full Moon in Gemini — Beaver Moon",
    category: "Full Moon",
    description:
      "The Gemini full moon brings words, ideas, and the truth you've been afraid to say. Conversations may take unexpected turns. Choose curiosity over certainty — that's where the real insight lives.",
    advisorName: "Sanjay Kapoor",
    advisorMessage:
      "Gemini moon brings the conversation you've been avoiding. Tarot can help you see how it lands before you have it",
  },
  {
    date: "Dec 21, 2026",
    startDate: "2026-12-21",
    endDate: "2026-12-21",
    title: "Winter Solstice",
    category: "Solstice",
    description:
      "The longest night. The wheel turns. Everything you've learned this year condenses into the seed of what comes next. Honor the stillness — then begin to dream forward.",
    advisorName: "Arjun Verma",
    advisorMessage:
      "Year ends, planets keep moving. Let's look at what 2026 actually shifted in your chart — and what 2027 wants from you",
  },
  {
    date: "Dec 24, 2026",
    startDate: "2026-12-24",
    endDate: "2026-12-24",
    title: "Full Moon in Cancer — Cold Moon (Xmas Eve)",
    category: "Full Moon",
    description:
      "A Cancer full moon during the holidays brings family, home, and old emotions to the surface. Whatever's unresolved in those threads will be loud this week. Receive it gently.",
    advisorName: "Vanessa Cole",
    advisorMessage:
      "Cancer moon, holiday season — that's a lot of feelings on one plate. Want to talk through what's surfacing? No pressure",
  },
]

import { getAppNow } from "../lib/get-app-now"

/** ISO calendar day (UTC) for inclusive date compares. */
function toDayKey(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Event whose date range includes today, or `undefined` if none. */
export function getActiveAstrologyEvent(
  now = getAppNow(),
): AstrologyEvent | undefined {
  const today = toDayKey(now)
  return ASTROLOGY_EVENTS.find(
    (e) => e.startDate <= today && today <= e.endDate,
  )
}
